import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DistributionChannel, DistributionChannelType } from '../../api';
import { api, erpApi, wordpressApi } from '../../api';
import {
  createDistributionChannel,
  enableDistribution,
  getDistributionIngest,
  getDistributionOverview,
  listDistributionChannels,
  rotateDistributionIngestKey,
} from '../../api';
import { useResource } from '../../hooks';
import { ChannelSettingsDrawer } from '../../components/distribution/ChannelSettingsDrawer';
import { DistributionOverviewPanel } from '../../components/distribution/DistributionOverviewPanel';
import { DistributionProductDrawer } from '../../components/distribution/DistributionProductDrawer';
import {
  DistributionProductsPanel,
  emptyProductFilter,
} from '../../components/distribution/DistributionProductsPanel';
import type { ProductFilter } from '../../components/distribution/DistributionProductsPanel';
import { channelLabel, relativeWhen } from '../../components/distribution/labels';
import { LevelBadge, PanelCard, ProjectSection, SectionHeader } from '../../components/project/ui';
import { EmptyState, ErrorNotice, SkeletonPage } from '../../components/ui';
import { useProject } from './ProjectContext';
import { Field, Select } from '../../components/primitives';

/**
 * Product distribution: what this shop publishes to the outside world.
 *
 * Magento owns what a product is. This section owns the other half — where
 * each product has been published, which version, whether the channel
 * accepted it, and what to fix when it did not.
 */
export function DistributionSection() {
  const project = useProject();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reloadToken, setReloadToken] = useState(0);
  const [filter, setFilter] = useState<ProductFilter>(emptyProductFilter);

  const workspace = useResource(api.workspace, [], undefined, { key: 'workspace' });
  const overview = useResource(
    () => getDistributionOverview(project.projectId),
    [project.projectId, reloadToken],
    project.projectId, { key: `project:${project.projectId}:distribution-overview` },
  );
  const channels = useResource(
    () => listDistributionChannels(project.projectId),
    [project.projectId, reloadToken],
    project.projectId, { key: `project:${project.projectId}:distribution-channels` },
  );

  const refresh = useCallback(() => setReloadToken((value) => value + 1), []);

  const openProduct = searchParams.get('product');
  const openChannel = searchParams.get('channel');

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (value === null) next.delete(key);
      else next.set(key, value);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const websites = useMemo(
    () =>
      (overview.data?.markets ?? []).reduce<{ code: string; name: string }[]>((list, market) => {
        if (!list.some((entry) => entry.code === market.website_code)) {
          list.push({ code: market.website_code, name: market.website_name });
        }
        return list;
      }, []),
    [overview.data],
  );

  const selectedChannel: DistributionChannel | undefined = useMemo(
    () => channels.data?.channels.find((channel) => channel.id === openChannel),
    [channels.data, openChannel],
  );

  // The server re-checks every mutation regardless; this only decides
  // whether a control is offered at all, so a read-only member is not shown
  // buttons that would be refused.
  const identity = workspace.data?.identity;
  const canManage = Boolean(
    identity?.role?.is_owner_role ||
      identity?.capabilities?.includes('manage_distribution') ||
      // The local development workspace has every capability server-side
      // (see RequestIdentity.HasCapability); the UI matches it so a
      // developer is not shown a read-only screen.
      identity?.kind === 'local_development_workspace',
  );

  if (overview.loading && !overview.data) return <SkeletonPage cards={3} rows={4} />;
  if (overview.error) {
    return (
      <ProjectSection>
        <ErrorNotice error={overview.error} />
      </ProjectSection>
    );
  }

  if (!overview.data?.enabled) {
    return (
      <ProjectSection>
        <DistributionSetup
          projectId={project.projectId}
          environmentId={project.environment?.id ?? null}
          onEnabled={refresh}
          canManage={canManage}
          isWordPress={project.isWordPress}
        />
      </ProjectSection>
    );
  }

  return (
    <ProjectSection>
      {overview.data.warnings?.map((warning) => (
        <PanelCard key={warning} tone="warning">
          <p className="panel__note">{warning}</p>
        </PanelCard>
      ))}

      <DistributionOverviewPanel
        overview={overview.data}
        projectId={project.projectId}
        canManage={canManage}
        onOpenChannel={(connectionId) => setParam('channel', connectionId)}
        onOpenProducts={(next) => setFilter({ ...emptyProductFilter, ...next })}
        onRefresh={refresh}
      />

      <DistributionProductsPanel
        projectId={project.projectId}
        channels={overview.data.channels}
        websites={websites}
        filter={filter}
        onFilterChange={setFilter}
        onOpenProduct={(projectionId) => setParam('product', projectionId)}
      />

      <AddChannel
        projectId={project.projectId}
        websites={websites}
        loading={channels.loading && !channels.data}
        readError={channels.error}
        onRetry={channels.reload}
        existing={channels.data?.channels ?? []}
        available={channels.data?.available ?? []}
        canManage={canManage}
        onAdded={(connectionId) => {
          refresh();
          setParam('channel', connectionId);
        }}
      />

      {project.isWordPress ? (
        <WooCommerceConnection projectId={project.projectId} canManage={canManage} lastEventAt={overview.data.source?.last_event_at} />
      ) : (
        <MagentoConnection projectId={project.projectId} canManage={canManage} lastEventAt={overview.data.source?.last_event_at} />
      )}

      {openProduct ? (
        <DistributionProductDrawer
          projectionId={openProduct}
          canManage={canManage}
          onClose={() => setParam('product', null)}
          onChanged={refresh}
        />
      ) : null}

      {selectedChannel ? (
        <ChannelSettingsDrawer
          channel={selectedChannel}
          canManage={canManage}
          onClose={() => setParam('channel', null)}
          onChanged={refresh}
        />
      ) : null}
    </ProjectSection>
  );
}

/** DistributionSetup is the first screen: nothing is configured yet. */
function DistributionSetup({
  projectId,
  environmentId,
  onEnabled,
  canManage,
  isWordPress,
}: {
  projectId: string;
  environmentId: string | null;
  onEnabled: () => void;
  canManage: boolean;
  isWordPress: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!environmentId) {
    return (
      <EmptyState
        title="No environment selected"
        description="Add an environment in Settings first."
      />
    );
  }

  return (
    <PanelCard labelledBy="distribution-setup-title">
      <SectionHeader id="distribution-setup-title" title="Publish this shop’s products" />
      <ul className="distribution-setup-steps">
        {isWordPress ? (
          <li>
            Each variation is its own listing, with its own price, stock and identifiers; a GTIN is never copied from a
            parent product to its variations.
          </li>
        ) : (
          <li>One product sold on two websites is two listings, with their own prices and URLs.</li>
        )}
      </ul>
      {error ? <p className="panel__note panel__note--danger">{error}</p> : null}
      {canManage ? (
        <button
          type="button"
          className="button button--primary"
          disabled={busy}
          data-testid="distribution-enable"
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await enableDistribution(projectId, environmentId);
              onEnabled();
            } catch (cause) {
              setError(cause instanceof Error ? cause.message : String(cause));
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Setting up' : 'Set up product distribution'}
        </button>
      ) : (
        <p className="quiet-empty">Ask a project owner to set up product distribution.</p>
      )}
    </PanelCard>
  );
}

/** AddChannel connects a channel to one website. */
function AddChannel({
  projectId,
  websites,
  loading,
  readError,
  onRetry,
  existing,
  available,
  canManage,
  onAdded,
}: {
  projectId: string;
  websites: { code: string; name: string }[];
  /** The channel list is being read: what is connected is not known yet. */
  loading: boolean;
  /** The channel list could not be read: "no channels" must not be assumed. */
  readError: Error | null;
  onRetry: () => void;
  existing: DistributionChannel[];
  available: { channel_type: DistributionChannelType; display_name: string }[];
  canManage: boolean;
  onAdded: (connectionId: string) => void;
}) {
  const [channelType, setChannelType] = useState<DistributionChannelType | ''>('');
  const [website, setWebsite] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Setup, not daily work (UX-19): once a channel exists the form waits
  // behind one button instead of sitting open between the products.
  // Derived, not initial state: the channel list loads after the first render.
  const [opened, setOpen] = useState(false);
  const open = opened || existing.length === 0;

  if (!canManage) return null;

  // Not knowing which channels exist is not the same as having none: the
  // form below would offer to connect what may already be connected.
  if (readError && existing.length === 0) {
    return (
      <PanelCard labelledBy="distribution-add-title" testId="distribution-channels-error">
        <SectionHeader id="distribution-add-title" title="Connect a channel" />
        <ErrorNotice error={readError} onRetry={onRetry} nextStep="The connected channels could not be read, so none is offered for connecting until they can." />
      </PanelCard>
    );
  }
  if (loading) return null;

  if (!open) {
    return (
      <PanelCard labelledBy="distribution-add-title">
        <SectionHeader
          id="distribution-add-title"
          title="Connect another channel"
          actions={
            <button type="button" className="button button--secondary" onClick={() => setOpen(true)} data-testid="distribution-add-open">
              Connect a channel
            </button>
          }
        />
      </PanelCard>
    );
  }

  const taken = new Set(existing.map((channel) => `${channel.channel_type}:${channel.website_code}`));
  const canAdd = channelType !== '' && website !== '' && !taken.has(`${channelType}:${website}`);

  return (
    <PanelCard labelledBy="distribution-add-title">
      <SectionHeader id="distribution-add-title" title="Connect a channel" />
      {error ? <p className="panel__note panel__note--danger">{error}</p> : null}
      <div className="distribution-add">
        <Field label="Channel">
          <Select
            id="add-channel-type"
            value={channelType}
            onChange={(event) => setChannelType(event.target.value as DistributionChannelType | '')}
          >
            <option value="">Choose a channel</option>
            {available.map((entry) => (
              <option key={entry.channel_type} value={entry.channel_type}>
                {channelLabel[entry.channel_type] ?? entry.display_name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Website">
          <Select id="add-channel-website" value={website} onChange={(event) => setWebsite(event.target.value)}>
            <option value="">Choose a website</option>
            {websites.map((entry) => (
              <option key={entry.code} value={entry.code}>
                {entry.name || entry.code}
              </option>
            ))}
          </Select>
        </Field>
        <button
          type="button"
          className="button button--secondary"
          disabled={!canAdd || busy}
          data-testid="distribution-add-channel"
          onClick={async () => {
            if (channelType === '') return;
            setBusy(true);
            setError(null);
            try {
              const created = await createDistributionChannel(projectId, {
                channel_type: channelType,
                website_code: website,
              });
              setChannelType('');
              setWebsite('');
              onAdded(created.id);
            } catch (cause) {
              setError(cause instanceof Error ? cause.message : String(cause));
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Connecting' : 'Connect'}
        </button>
      </div>
      {channelType && website && taken.has(`${channelType}:${website}`) ? (
        <p className="quiet-empty">That channel is already connected to this website.</p>
      ) : null}
    </PanelCard>
  );
}

/**
 * WooCommerceConnection keeps the catalogue in step: WooCommerce product
 * webhooks announce each change, signed with the source key, and the
 * scheduled reconciliation catches whatever a webhook missed.
 */
function WooCommerceConnection({ projectId, canManage, lastEventAt }: { projectId: string; canManage: boolean; lastEventAt?: string }) {
  // The Commerce Ops for WooCommerce plugin (CO-137) keeps its own outbox of
  // product changes; while it reports in, WooCommerce's webhooks are only a
  // fallback (Commerce Ops skips their deliveries).
  const stores = useResource(() => erpApi.projectStores(projectId), [projectId], projectId, { key: `project:${projectId}:erp-stores` });
  const pluginStores = (stores.data?.stores ?? []).filter(
    (store) => store.platform === 'woocommerce' && store.status === 'active' && store.settings.distribution?.outbox === true,
  );
  const plugin = pluginStores.find((store) => store.last_seen_at !== undefined && Date.now() - new Date(store.last_seen_at).getTime() < 24 * 60 * 60 * 1000);
  // A plugin that reported in once but not within a day is silent, and that is
  // a state, not an absence (IN7).
  const silentPlugin = plugin ? undefined : pluginStores[0];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [result, setResult] = useState<{ created: string[]; kept: string[]; reactivated: string[] } | null>(null);

  async function ensure() {
    setBusy(true);
    setError(null);
    try {
      const res = await wordpressApi.configureWooCommerceWebhooks(projectId);
      setResult({ created: res.created ?? [], kept: res.kept ?? [], reactivated: res.reactivated ?? [] });
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <PanelCard labelledBy="distribution-woocommerce-title" testId="distribution-woocommerce">
      <SectionHeader
        id="distribution-woocommerce-title"
        title="Changes from the shop"
        helpLabel="How changes arrive"
        help={
          plugin
            ? 'The Commerce Ops for WooCommerce plugin records every product change, including permanent deletes, stock sold by orders and category edits, and sends it within a minute. Nothing is lost while Commerce Ops is unreachable. A scheduled reconciliation still re-reads the catalogue.'
            : 'WooCommerce sends a signed webhook when a product is created, updated, deleted or restored, and Commerce Ops re-reads just that product. A scheduled reconciliation re-reads the catalogue, so a missed webhook delays a change rather than losing it.'
        }
        actions={
          canManage ? (
            <button type="button" className="button button--secondary" onClick={ensure} disabled={busy} data-testid="woocommerce-webhooks">
              {busy ? 'Setting up' : plugin ? 'Set up fallback webhooks' : 'Set up webhooks'}
            </button>
          ) : null
        }
      />
      {plugin ? (
        <p className="panel__note" data-testid="distribution-woocommerce-plugin">
          <LevelBadge level="ok">Plugin delivering</LevelBadge> {plugin.name} · last report {relativeWhen(plugin.last_seen_at)}
          {plugin.settings.distribution?.read_api ? ' · reads the catalogue' : ''}
        </p>
      ) : stores.error && !stores.data ? (
        <ErrorNotice error={stores.error} onRetry={stores.reload} nextStep="Whether the plugin is delivering could not be read." />
      ) : silentPlugin ? (
        <p className="panel__note" data-testid="distribution-woocommerce-plugin-silent">
          <LevelBadge level="warning">Plugin silent</LevelBadge> {silentPlugin.name} · last report {relativeWhen(silentPlugin.last_seen_at)}.
          WooCommerce’s own webhooks count again.
        </p>
      ) : null}
      {lastEventAt ? (
        <p className="panel__note muted" data-testid="distribution-last-change">
          Last change received · {relativeWhen(lastEventAt)}
        </p>
      ) : null}
      <ErrorNotice error={error} />
      {result ? (
        <p className="panel__note" role="status">
          {result.created.length > 0 ? `Created: ${result.created.join(', ')}. ` : ''}
          {result.reactivated.length > 0
            ? `Switched back on (WooCommerce had disabled them after failed deliveries): ${result.reactivated.join(', ')}. `
            : ''}
          {result.kept.length > 0 ? `Already in place: ${result.kept.join(', ')}.` : ''}
          {result.created.length === 0 && result.kept.length === 0 && result.reactivated.length === 0
            ? 'No webhooks were reported by the shop.'
            : ''}
        </p>
      ) : null}
    </PanelCard>
  );
}

/**
 * MagentoConnection shows what the shop's own module needs, so an operator
 * can set it up without leaving the page. The signing key is shown once,
 * when it is created, and never again.
 */
function MagentoConnection({ projectId, canManage, lastEventAt }: { projectId: string; canManage: boolean; lastEventAt?: string }) {
  const ingest = useResource(() => getDistributionIngest(projectId), [projectId], projectId, { key: `project:${projectId}:distribution-ingest` });
  const [key, setKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <PanelCard labelledBy="distribution-magento-title">
      <SectionHeader
        id="distribution-magento-title"
        title="Changes from the shop"
        helpLabel="How changes arrive"
        help="The Commerce Ops module in Magento records every catalogue change and sends it here. A product save never waits for Commerce Ops, and an outage delays distribution rather than losing it."
      />
      {error ? <p className="panel__note panel__note--danger">{error}</p> : null}
      {ingest.error && !ingest.data ? <ErrorNotice error={ingest.error} onRetry={ingest.reload} nextStep="The endpoint and the state of the signing key could not be read." /> : null}
      {ingest.data?.key_configured ? (
        // A key is not a connection: whether the module delivers is its own fact.
        <p className="panel__note" data-testid="distribution-module-contact">
          {lastEventAt
            ? `Last change received · ${relativeWhen(lastEventAt)}`
            : 'No change received yet. An issued key does not show that the module uses it.'}
        </p>
      ) : null}
      {ingest.data?.key_configured ? (
        <details className="quiet-disclosure" data-testid="distribution-connection-details">
          <summary>Signing key issued. Endpoint, source id and key</summary>
          <ConnectionFacts url={ingest.data.url} sourceId={ingest.data.source_id} keyConfigured />
        </details>
      ) : ingest.data ? (
        <ConnectionFacts url={ingest.data.url} sourceId={ingest.data.source_id} keyConfigured={false} />
      ) : null}

      {key ? (
        <div className="distribution-key">
          <p className="panel__note">Copy this key into the Magento module now. Commerce Ops will not show it again.</p>
          <code className="distribution-key__value">{key}</code>
        </div>
      ) : null}

      {canManage ? (
        <button
          type="button"
          className="button button--secondary"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              const result = await rotateDistributionIngestKey(projectId);
              setKey(result.key);
              ingest.reload();
            } catch (cause) {
              setError(cause instanceof Error ? cause.message : String(cause));
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Issuing' : ingest.data?.key_configured ? 'Issue a new signing key' : 'Issue a signing key'}
        </button>
      ) : null}
    </PanelCard>
  );
}

/** ConnectionFacts: what the Magento module needs to send changes here. */
function ConnectionFacts({ url, sourceId, keyConfigured }: { url: string; sourceId: string; keyConfigured: boolean }) {
  return (
    <dl className="fact-list">
      <div className="fact-list__row">
        <dt>Endpoint</dt>
        <dd>
          <code>{url}</code>
        </dd>
      </div>
      <div className="fact-list__row">
        <dt>Source id</dt>
        <dd>
          <code>{sourceId}</code>
        </dd>
      </div>
      <div className="fact-list__row">
        <dt>Signing key</dt>
        <dd>{keyConfigured ? 'Issued' : 'Not issued yet'}</dd>
      </div>
    </dl>
  );
}
